// File: src/components/teacher/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Tabs, 
  Tab,
  Alert
} from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services';
import { TeacherDashboardData, User } from '@/types/models';
import { dashboardStyles } from '@/layouts/TeacherLayout';
import OverviewTab from '@/components/teacher/dashboard/OverviewTab';
import StudentsTab from '@/components/teacher/dashboard/StudentsTab';
import AnalyticsTab from '@/components/teacher/dashboard/AnalyticsTab';
import ApprovalsTab from '@/components/teacher/dashboard/ApprovalsTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`dashboard-tabpanel-${index}`}
    aria-labelledby={`dashboard-tab-${index}`}
    sx={{ flex: 1 }}
  >
    {value === index && children}
  </Box>
);

const TeacherDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState<TeacherDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth() as { user: User | null };

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setIsLoading(true);
        const response = await api.dashboard.getTeacherDashboard();
        if (response?.data) {
          setDashboardData(response.data as TeacherDashboardData);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box m={2}>
        <Alert severity="warning">No dashboard data available</Alert>
      </Box>
    );
  }

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header Section */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Welcome, {user?.first_name}! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your classroom and track student progress
        </Typography>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={dashboardStyles.dashboard.tabs.wrapper}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': dashboardStyles.dashboard.tabs.tab
          }}
        >
          <Tab label="Overview" />
          <Tab label="Students" />
          <Tab label="Analytics" />
          <Tab 
            label={`Approvals ${dashboardData.class_stats.pending_approvals > 0 
              ? `(${dashboardData.class_stats.pending_approvals})` 
              : ''}`} 
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <OverviewTab data={dashboardData} />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <StudentsTab data={dashboardData} />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <AnalyticsTab data={dashboardData} />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <ApprovalsTab data={dashboardData} />
      </TabPanel>
    </Box>
  );
};

export default TeacherDashboard;
